import React, { useRef, useState, useContext } from "react";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { store } from "../../../context/store";
import { JWT } from "../../../constants/local-storage";
import ScreenCapturePanelAssetFinder from "./ScreenCapturePanelAssetFinder";
import ScreenCapturePanelReview from "./ScreenCapturePanelReview";
import ScreenCapturePanelRefurbishment from "./ScreenCapturePanelRefurbishment";
import ScreenCapturePanelWorkplace from "./ScreenCapturePanelWorkplace";
import ScreenCapturePanelSustainability from "./ScreenCapturePanelSustainability";
import ScreenCapturePanelAccessibility from "./ScreenCapturePanelAccessibility";
import normaliseSurveyResults
    from "./normalisers";

export default function ScreenCapture() {

    const globalState = useContext(store);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [surveyResults, setSurveyResults] = useState([]);
    
	const [hoveredId, setHoveredId] = useState(null);
	
	const [surveyType, setSurveyType] = useState("ASSET");
	const [liveSurvey, setLiveSurvey] = useState(false);

    function captureScreen() {

        navigator.mediaDevices.getDisplayMedia({
            video: true
        }).then(function (stream) {

            const video = videoRef.current;
            video.srcObject = stream;

            video.onloadedmetadata = function () {

                video.play();

                setTimeout(function () {

                    const canvas = canvasRef.current;
                    const context = canvas.getContext("2d");

                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    context.drawImage(video, 0, 0, canvas.width, canvas.height);

                    const dataUrl = canvas.toDataURL("image/jpeg", 0.6);

                    setImage(dataUrl);

                    stream.getTracks().forEach(function (track) {
                        track.stop();
                    });

                    sendToBackend(dataUrl);

                }, 300);
            };

        }).catch(function (err) {
            console.error("Screen capture failed:", err);
        });
    }
	
	
	

    function sendToBackend(imageData) {

        setLoading(true);

        fetch("/resolutionsoftwareplatform/aiassetfinder/scan", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + localStorage.getItem(JWT)
            },
			body: JSON.stringify({

			    image: imageData,

			    surveyType: surveyType,

			    live: liveSurvey

			})
        })
        .then(function (response) {

            if (!response.ok) {
                throw new Error("Request failed: " + response.status);
            }

            return response.json();
        })
        .then(function (data) {

            console.log("🔥 AI RESULTS:", data);
			setSurveyResults(
			       normaliseSurveyResults(
			           data,
			           surveyType
			       )
			   );

        })
        .catch(function (err) {
            console.error("AI SCAN ERROR:", err);
        })
        .finally(function () {
            setLoading(false);
        });
    }

    function getColour(type) {

        if (!type) return "#999";

        const lowerType = type.toLowerCase();

        if (lowerType.indexOf("desk") >= 0) return "#5a9bd6";
        if (lowerType.indexOf("chair") >= 0) return "#5AC26A";
        if (lowerType.indexOf("monitor") >= 0) return "#E24E6B";
        if (lowerType.indexOf("breakout") >= 0) return "#F5A623";
        if (lowerType.indexOf("light") >= 0) return "#FFD166";
        if (lowerType.indexOf("smoke") >= 0) return "#C084FC";
        if (lowerType.indexOf("diffuser") >= 0) return "#7DD3FC";
        if (lowerType.indexOf("sensor") >= 0) return "#FB7185";

        return "#999";
    }

    function getConditionColour(condition, type) {

        if (!condition) {
            return getColour(type);
        }

        switch (condition.toLowerCase()) {
            case "excellent":
                return "#4CAF50";
            case "good":
                return "#8BC34A";
            case "fair":
                return "#FFC107";
            case "poor":
                return "#FF5722";
            case "critical":
                return "#F44336";
            default:
                return getColour(type);
        }
    }

    function hasCoordinates(a) {
        return a && a.x != null && a.y != null;
    }

    return (

        <div style={{ padding: "20px", position: "relative" }}>

            {/* BUTTON */}
			{/* AI SURVEY TOOLBAR */}

			<div
			    style={{
			        display: "flex",
			        alignItems: "center",
			        gap: "15px",
			        marginBottom: "20px",
			        padding: "12px 16px",
			        background: "rgba(255,255,255,0.05)",
			        border: "1px solid rgba(255,255,255,0.08)",
			        borderRadius: "10px",
			        backdropFilter: "blur(10px)"
			    }}
			>

			    <button
			        onClick={captureScreen}
			        style={{
			            background: "#5a9bd6",
			            color: "#fff",
			            border: "none",
			            padding: "12px 20px",
			            borderRadius: "8px",
			            cursor: "pointer",
			            display: "flex",
			            alignItems: "center",
			            gap: "10px",
			            fontWeight: "bold",
			            fontSize: "15px",
			            minWidth: "170px",
			            justifyContent: "center",
			            opacity: loading ? 0.6 : 1
			        }}
			    >
			        <FontAwesomeIcon icon={faCamera} />

			        {loading ? "Surveying..." : "AI Survey"}

			    </button>

			    <select
			        value={surveyType}
			        onChange={function(e)
			        {
			            setSurveyType(e.target.value);
			        }}
			        style={{
			            height: "44px",
			            minWidth: "280px",
			            background: "#1d1d1d",
			            color: "#fff",
			            border: "1px solid #444",
			            borderRadius: "8px",
			            padding: "0 12px",
			            fontSize: "14px",
			            outline: "none"
			        }}
			    >
			        <option value="ASSET">
			            Asset Survey
			        </option>

			        <option value="COMPLIANCE">
			            Compliance Survey
			        </option>

			        <option value="REFURBISHMENT">
			            Refurbishment Planner
			        </option>

			        <option value="WORKPLACE">
			            Workplace Assessment
			        </option>

			        <option value="SUSTAINABILITY">
			            Sustainability Review
			        </option>

			        <option value="ACCESSIBILITY">
			            Accessibility Review
			        </option>

			        <option value="FIRE">
			            Fire Safety Review
			        </option>

			    </select>

			    <label
			        style={{
			            display: "flex",
			            alignItems: "center",
			            gap: "10px",
			            color: "#fff",
			            cursor: "pointer",
			            fontSize: "14px",
			            userSelect: "none"
			        }}
			    >

			        <input
			            type="checkbox"
			            checked={liveSurvey}
			            onChange={function(e)
			            {
			                setLiveSurvey(e.target.checked);
			            }}
			            style={{
			                width: "18px",
			                height: "18px",
			                cursor: "pointer"
			            }}
			        />

			        Live OpenAI

			    </label>

			</div>

            {/* VIDEO + CANVAS */}
            <video ref={videoRef} style={{ display: "none" }} />
            <canvas ref={canvasRef} style={{ display: "none" }} />

            {/* 🔥 FULL SCREEN LOADING */}
            {loading && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.9)",
                    zIndex: 9999,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#fff"
                }}>
                    <div style={{
                        width: "90px",
                        height: "90px",
                        border: "6px solid #333",
                        borderTop: "6px solid #5a9bd6",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                    }} />
                    <div style={{ marginTop: "20px", fontSize: "20px" }}>
                        AI Analysing Environment...
                    </div>
                </div>
            )}

            {/* 🔥 FULL SCREEN RESULT MODE */}
            {image && !loading && (

                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "#000",
                    zIndex: 9998,
                    display: "flex"
                }}>

                    {/* CLOSE */}
                    <div
                        onClick={function () {
                            setImage(null);
                            setSurveyResults([]);
                        }}
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            width: "42px",
                            height: "42px",
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.1)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "22px",
                            zIndex: 10000
                        }}
                    >
                        ✕
                    </div>

                    {/* IMAGE */}
                    <div style={{
                        flex: 1,
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden"
                    }}>

                        <img
                            ref={imageRef}
                            src={image}
                            alt="capture"
                            style={{ width: "100%", display: "block" }}
                        />

                        {/* HOTSPOTS - only render where x/y exist, so OpenAI-only results still work */}
                        {surveyResults
                            .filter(function (a) {
                                return hasCoordinates(a);
                            })
                            .map(function (a) {

                                if (!imageRef.current) return null;

								const img = imageRef.current;
								const container = img.parentElement;

								const imgRect = img.getBoundingClientRect();
								const containerRect = container.getBoundingClientRect();

								let x;
								let y;

								if (a.x <= 100 && a.y <= 100)
								{
								    // GPT percentage coordinates
								    x =
								        (imgRect.left - containerRect.left) +
								        (imgRect.width * (a.x / 100));

								    y =
								        (imgRect.top - containerRect.top) +
								        (imgRect.height * (a.y / 100));
								}
								else
								{
								    // YOLO pixel coordinates
								    const scaleX = imgRect.width / img.naturalWidth;
								    const scaleY = imgRect.height / img.naturalHeight;

								    x =
								        (imgRect.left - containerRect.left) +
								        (a.x * scaleX);

								    y =
								        (imgRect.top - containerRect.top) +
								        (a.y * scaleY);
								}

                                const isHovered = hoveredId === a.id;
                                const colour = getConditionColour(a.condition, a.type);

                                return (
                                    <div
                                        key={a.id}
                                        onMouseEnter={function () { setHoveredId(a.id); }}
                                        onMouseLeave={function () { setHoveredId(null); }}
                                        style={{
                                            position: "absolute",
                                            left: x,
                                            top: y,
                                            transform: "translate(-50%, -50%)",
                                            cursor: "pointer"
                                        }}
                                    >

                                        {/* 🔥 SUBTLE BASE DOT (always visible) */}
                                        <div style={{
                                            width: "6px",
                                            height: "6px",
                                            background: colour,
                                            borderRadius: "50%",
                                            opacity: isHovered ? 1 : 0.4,
                                            position: "absolute",
                                            transform: "translate(-50%, -50%)"
                                        }} />

                                        {/* 💥 ACTIVE GLOW ONLY */}
                                        {isHovered && (
                                            <>
                                                {/* OUTER GLOW */}
                                                <div style={{
                                                    position: "absolute",
                                                    width: "46px",
                                                    height: "46px",
                                                    background: colour,
                                                    borderRadius: "50%",
                                                    opacity: 0.15,
                                                    transform: "translate(-50%, -50%)",
                                                    animation: "pulseRing 1.6s infinite"
                                                }} />

                                                {/* INNER CORE */}
                                                <div style={{
                                                    position: "absolute",
                                                    width: "14px",
                                                    height: "14px",
                                                    background: colour,
                                                    borderRadius: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    boxShadow: "0 0 18px " + colour
                                                }} />

                                                {/* LABEL */}
                                                <div style={{
                                                    position: "absolute",
                                                    top: "-58px",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    background: "rgba(0,0,0,0.9)",
                                                    color: "#fff",
                                                    padding: "8px 10px",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    whiteSpace: "nowrap",
                                                    boxShadow: "0 0 12px rgba(0,0,0,0.6)"
                                                }}>
                                                    <div>{a.type} ({Math.round(a.confidence * 100)}%)</div>
                                                    {a.condition && (
                                                        <div style={{ color: colour, marginTop: "3px" }}>
                                                            {a.condition}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                    </div>
                                );
                            })}

                    </div>

					{
					    surveyType === "ASSET" &&

					    <ScreenCapturePanelAssetFinder
					        assets={surveyResults}
					        hasCoordinates={hasCoordinates}
					        getConditionColour={getConditionColour}
					        hoveredId={hoveredId}
					        setHoveredId={setHoveredId}
					    />
					}

					{
					    surveyType === "REFURBISHMENT" &&

					    <ScreenCapturePanelRefurbishment
					        recommendations={surveyResults}
					        hasCoordinates={hasCoordinates}
					        hoveredId={hoveredId}
					        setHoveredId={setHoveredId}
					    />
					}

					{
					    surveyType === "WORKPLACE" &&

					    <ScreenCapturePanelWorkplace
					        recommendations={surveyResults}
					        hasCoordinates={hasCoordinates}
					        hoveredId={hoveredId}
					        setHoveredId={setHoveredId}
					    />
					}

					{
					    surveyType === "SUSTAINABILITY" &&

					    <ScreenCapturePanelSustainability
					        recommendations={surveyResults}
					        hasCoordinates={hasCoordinates}
					        hoveredId={hoveredId}
					        setHoveredId={setHoveredId}
					    />
					}

					{
					    surveyType === "ACCESSIBILITY" &&

					    <ScreenCapturePanelAccessibility
					        recommendations={surveyResults}
					        hasCoordinates={hasCoordinates}
					        hoveredId={hoveredId}
					        setHoveredId={setHoveredId}
					    />
					}

					{
					    surveyType !== "ASSET" &&
					    surveyType !== "REFURBISHMENT" &&
					    surveyType !== "WORKPLACE" &&
					    surveyType !== "SUSTAINABILITY" &&
					    surveyType !== "ACCESSIBILITY" &&

					    <ScreenCapturePanelReview
					        issues={surveyResults}
					        hasCoordinates={hasCoordinates}
					        hoveredId={hoveredId}
					        setHoveredId={setHoveredId}
					    />
					}

                </div>
            )}

            {/* ANIMATIONS */}
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes pulse {
                    0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                    50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.7; }
                    100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                }

                @keyframes pulseRing {
                    0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.25; }
                    70% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
                    100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
                }
                `}
            </style>

        </div>
    );
}
